import {
  Flower2, Heart, Layers, Sun, Leaf, Sprout, Sparkles, Gift, Flower, User,
} from 'lucide-react';

import { collectionUrl } from '@/lib/collectionUrl';
import imgFlowerBox from '@assets/generated_images/category-flower-box.jpg';
import imgFlowerBoxPrem from '@assets/generated_images/product-flower-box-premium.jpg';
import type { CategorySeoShowcaseConfig } from '@/components/CategorySeoShowcase';

/**
 * Dane dla pięciu wariantów `CategorySeoShowcase` — przepisane 1:1 z
 * usuniętych `KoszeSeoSection.tsx`, `TorebkaSeoSection.tsx`,
 * `KwiatySeoSection.tsx`, `FlowerboxSeoSection.tsx`, `BukietySeoSection.tsx`.
 * Żaden tekst, href, obraz, ikona ani kolejność nie zostały zmienione.
 */

const BASE = import.meta.env.BASE_URL;

export const KOSZE_SEO_CONFIG: CategorySeoShowcaseConfig = {
  sectionAriaLabel: 'Kosze kwiatowe na każdą okazję',
  eyebrow: 'O kwiatach',
  heading: 'Kosze kwiatowe, które wyrażają więcej',
  subtitle: [
    'Gotowe kompozycje pełne świeżości i harmonii.',
    'Idealne na prezent, podziękowanie lub po prostu – żeby sprawić radość.',
  ],
  cardMinWidth: 220,
  cardBoxShadow: '0 2px 14px rgba(42,31,20,0.06)',
  cards: [
    {
      Icon: Flower2,
      title: 'Kosze róż',
      desc: 'Ponadczasowe i eleganckie. Czerwone, różowe, białe i kremowe róże układają się w kompozycje pełne klasy.',
      image: `${BASE}kreator-roze.jpg`,
      imageAlt: 'Kosz kwiatowy - Kosze róż',
      linkLabel: 'Zobacz kosze róż',
      href: collectionUrl('roze'),
    },
    {
      Icon: Sprout,
      title: 'Kosze tulipanów',
      desc: 'Świeże, kolorowe i pełne uroku. Idealne na urodziny, imieniny lub jako wiosenny gest.',
      image: `${BASE}kreator-tulipany.jpg`,
      imageAlt: 'Kosz kwiatowy - Kosze tulipanów',
      linkLabel: 'Zobacz kosze tulipanów',
      href: collectionUrl('tulipany'),
    },
    {
      Icon: Layers,
      title: 'Kosze mieszane',
      desc: 'Różnorodne i harmonijne kompozycje łączące najpiękniejsze kwiaty sezonu. Na każdą okazję.',
      image: `${BASE}kreator-gerbery.jpg`,
      imageAlt: 'Kosz kwiatowy - Kosze mieszane',
      linkLabel: 'Zobacz kosze mieszane',
      href: collectionUrl('kosze-kwiatowe'),
    },
    {
      Icon: Sun,
      title: 'Kosze sezonowe',
      desc: 'Kompozycje tworzone z najpiękniejszych kwiatów dostępnych w danym sezonie. Zawsze świeże, zawsze wyjątkowe.',
      image: `${BASE}kreator-sloneczniki.jpg`,
      imageAlt: 'Kosz kwiatowy - Kosze sezonowe',
      linkLabel: 'Zobacz kosze sezonowe',
      href: collectionUrl('kosze-kwiatowe'),
    },
  ],
  pillsLabel: 'Najczęściej wybierane kwiaty',
  pills: [
    { Icon: Flower2, label: 'Róże', href: collectionUrl('roze') },
    { Icon: Sprout, label: 'Tulipany', href: collectionUrl('tulipany') },
    { Icon: Leaf, label: 'Lilie', href: collectionUrl('lilie') },
    { Icon: Heart, label: 'Hortensje', href: collectionUrl('hortensje') },
    { Icon: Sparkles, label: 'Goździki', href: collectionUrl('gozdziki') },
    { Icon: Flower2, label: 'Eustomy', href: collectionUrl('eustomy') },
    { Icon: Layers, label: 'Wszystkie kwiaty', href: collectionUrl('kwiaty') },
  ],
};

export const TOREBKA_SEO_CONFIG: CategorySeoShowcaseConfig = {
  sectionAriaLabel: 'Kwiaty w torebce na każdą okazję',
  eyebrow: 'O kwiatach',
  heading: 'Kwiaty w torebce, które zachwycają',
  subtitle: [
    'Nowoczesne kompozycje w eleganckiej torebce – gotowe, by wywołać uśmiech.',
    'Idealne na prezent, podziękowanie lub wyjątkowy gest bez okazji.',
  ],
  cardMinWidth: 220,
  cardBoxShadow: '0 2px 14px rgba(42,31,20,0.06)',
  cards: [
    {
      Icon: Flower2,
      title: 'Torebki róż',
      desc: 'Ponadczasowe i pełne klasy. Róże w pięknych odcieniach, które zawsze robią wrażenie.',
      image: `${BASE}kreator-roze.jpg`,
      imageAlt: 'Kwiaty w torebce - Torebki róż',
      linkLabel: 'Zobacz torebki róż',
      href: collectionUrl('roze'),
    },
    {
      Icon: Sprout,
      title: 'Torebki tulipanów',
      desc: 'Świeże, wiosenne i pełne uroku. Tulipany, które wyrażają więcej niż tysiąc słów.',
      image: `${BASE}kreator-tulipany.jpg`,
      imageAlt: 'Kwiaty w torebce - Torebki tulipanów',
      linkLabel: 'Zobacz torebki tulipanów',
      href: collectionUrl('tulipany'),
    },
    {
      Icon: Layers,
      title: 'Torebki mieszane',
      desc: 'Różnorodne kompozycje pełne kolorów i harmonii. Idealne na każdą okazję i dla każdego.',
      image: `${BASE}kreator-gerbery.jpg`,
      imageAlt: 'Kwiaty w torebce - Torebki mieszane',
      linkLabel: 'Zobacz torebki mieszane',
      href: collectionUrl('kwiaty-w-torebce'),
    },
    {
      Icon: Sun,
      title: 'Torebki sezonowe',
      desc: 'Kompozycje tworzone z najświeższych kwiatów dostępnych w danym sezonie. Zawsze wyjątkowe.',
      image: `${BASE}kreator-sloneczniki.jpg`,
      imageAlt: 'Kwiaty w torebce - Torebki sezonowe',
      linkLabel: 'Zobacz torebki sezonowe',
      href: collectionUrl('kwiaty-w-torebce'),
    },
  ],
  pillsLabel: 'Najczęściej wybierane kwiaty',
  pills: [
    { Icon: Flower2, label: 'Róże', href: collectionUrl('roze') },
    { Icon: Sprout, label: 'Tulipany', href: collectionUrl('tulipany') },
    { Icon: Leaf, label: 'Lilie', href: collectionUrl('lilie') },
    { Icon: Heart, label: 'Hortensje', href: collectionUrl('hortensje') },
    { Icon: Sparkles, label: 'Goździki', href: collectionUrl('gozdziki') },
    { Icon: Flower2, label: 'Eustomy', href: collectionUrl('eustomy') },
    { Icon: Layers, label: 'Wszystkie kwiaty', href: collectionUrl('kwiaty') },
  ],
};

export const KWIATY_SEO_CONFIG: CategorySeoShowcaseConfig = {
  sectionAriaLabel: 'Kwiaty, które mówią więcej',
  eyebrow: 'O kwiatach',
  heading: 'Kwiaty, które mówią więcej',
  subtitle: 'Świeże kwiaty to piękny sposób na wyrażenie emocji – od klasycznych róż po delikatne tulipany i pełne energii kompozycje. Wybierz swoje ulubione kwiaty.',
  cardMinWidth: 220,
  cardBoxShadow: '0 2px 12px rgba(42,31,20,0.06)',
  cards: [
    {
      Icon: Gift,
      title: 'Róże',
      desc: 'Ponadczasowe i pełne znaczenia. Czerwone, różowe, białe czy kremowe – róże sprawdzają się na każdą okazję.',
      image: `${BASE}kreator-roze.jpg`,
      imageAlt: 'Róże',
      linkLabel: 'Zobacz róże',
      href: collectionUrl('roze'),
    },
    {
      Icon: Flower,
      title: 'Tulipany',
      desc: 'Kolorowe, świeże i pełne uroku. Idealne na urodziny, imieniny lub po prostu miły gest.',
      image: `${BASE}kreator-tulipany.jpg`,
      imageAlt: 'Tulipany',
      linkLabel: 'Zobacz tulipany',
      href: collectionUrl('tulipany'),
    },
    {
      Icon: Flower2,
      title: 'Gerbery',
      desc: 'Radosne i pełne energii kwiaty, które od razu przyciągają spojrzenia. Świetne do kolorowych kompozycji.',
      image: `${BASE}kreator-gerbery.jpg`,
      imageAlt: 'Gerbery',
      linkLabel: 'Zobacz gerbery',
      href: collectionUrl('gerbery'),
    },
    {
      Icon: Leaf,
      title: 'Kwiaty sezonowe',
      desc: 'Wybieramy to, co w danym momencie najpiękniejsze i najświeższe. Kompozycje zmieniające się z porą roku.',
      image: `${BASE}bouquet-premium.png`,
      imageAlt: 'Kwiaty sezonowe',
      linkLabel: 'Zobacz kwiaty sezonowe',
      href: collectionUrl('kwiaty'),
    },
  ],
  pillsLabel: 'Najczęściej wybierane kwiaty',
  pills: [
    { Icon: Flower2, label: 'Róże', href: collectionUrl('roze') },
    { Icon: Flower, label: 'Tulipany', href: collectionUrl('tulipany') },
    { Icon: Flower2, label: 'Lilie', href: collectionUrl('lilie') },
    { Icon: Flower2, label: 'Hortensje', href: collectionUrl('hortensje') },
    { Icon: Flower, label: 'Goździki', href: collectionUrl('gozdziki') },
    { Icon: Flower2, label: 'Eustomy', href: collectionUrl('eustomy') },
    { Icon: Flower, label: 'Gipsówka', href: collectionUrl('gipsowka') },
    { Icon: Flower2, label: 'Margaretki', href: collectionUrl('margaretki') },
    { Icon: Flower, label: 'Piwonie', href: collectionUrl('piwonie') },
    { Icon: Flower2, label: 'Lewkonie', href: collectionUrl('lewkonie') },
    { Icon: Flower, label: 'Frezje', href: collectionUrl('frezje') },
    { Icon: Flower2, label: 'Jaskry', href: collectionUrl('jaskier') },
    { Icon: Flower, label: 'Anturium', href: collectionUrl('anturium') },
    { Icon: Flower2, label: 'Dalie', href: collectionUrl('dalie') },
    { Icon: Flower2, label: 'Lwie paszcze', href: collectionUrl('lwiepaszcze') },
    { Icon: Gift, label: 'Wszystkie kwiaty', href: collectionUrl('kwiaty') },
  ],
};

export const FLOWERBOX_SEO_CONFIG: CategorySeoShowcaseConfig = {
  sectionAriaLabel: 'Flowerboxy na każdą okazję',
  eyebrow: 'O flowerboxach',
  heading: 'Flowerboxy na każdą okazję',
  subtitle: [
    'Eleganckie kompozycje kwiatów w ozdobnym pudełku.',
    'Trwałe, efektowne i gotowe, by wywołać uśmiech w wyjątkowym momencie.',
  ],
  cardMinWidth: 200,
  cardBoxShadow: '0 2px 14px rgba(42,31,20,0.06)',
  cards: [
    {
      Icon: Gift,
      title: ['Na', 'urodziny'],
      desc: 'Kolorowe i radosne kompozycje, które sprawią przyjemność i podkreślą wyjątkowy dzień.',
      image: imgFlowerBox,
      imageAlt: 'Flowerbox Na urodziny',
      linkLabel: 'Zobacz flowerboxy urodzinowe',
      href: collectionUrl('urodziny'),
    },
    {
      Icon: Heart,
      title: ['Na', 'rocznicę'],
      desc: 'Romantyczne kompozycje pełne miłości i elegancji – idealne, by wyrazić to, co najważniejsze.',
      image: imgFlowerBoxPrem,
      imageAlt: 'Flowerbox Na rocznicę',
      linkLabel: 'Zobacz flowerboxy na rocznicę',
      href: collectionUrl('rocznica'),
    },
    {
      Icon: User,
      title: ['Dla', 'Mamy'],
      desc: 'Delikatne i pełne ciepła kompozycje, które będą pięknym podziękowaniem za wszystko.',
      image: imgFlowerBox,
      imageAlt: 'Flowerbox Dla Mamy',
      linkLabel: 'Zobacz flowerboxy dla Mamy',
      href: collectionUrl('dla-mamy'),
    },
    {
      Icon: Sparkles,
      title: ['Dla', 'niej'],
      desc: 'Subtelne i kobiece kompozycje, które zachwycają stylem i wywołują uśmiech.',
      image: imgFlowerBoxPrem,
      imageAlt: 'Flowerbox Dla niej',
      linkLabel: 'Zobacz flowerboxy dla niej',
      href: collectionUrl('dla-dziewczyny'),
    },
    {
      Icon: Flower2,
      title: ['Bez', 'okazji'],
      desc: 'Czasem nie potrzeba okazji, by sprawić komuś radość. Podaruj kwiaty prosto z serca!',
      image: imgFlowerBox,
      imageAlt: 'Flowerbox Bez okazji',
      linkLabel: 'Zobacz flowerboxy bez okazji',
      href: collectionUrl('niespodzianka'),
    },
  ],
  pillsLabel: 'Najczęściej wybierane',
  pills: [
    { Icon: Gift, label: 'Flowerboxy na urodziny', href: collectionUrl('urodziny') },
    { Icon: Heart, label: 'Flowerboxy na rocznicę', href: collectionUrl('rocznica') },
    { Icon: User, label: 'Flowerboxy dla Mamy', href: collectionUrl('dla-mamy') },
    { Icon: Flower2, label: 'Flowerboxy z róż', href: collectionUrl('roze') },
    { Icon: Sparkles, label: 'Flowerboxy w pastelach', href: collectionUrl('flowerbox') },
    { Icon: Gift, label: 'Wszystkie flowerboxy', href: collectionUrl('flowerbox') },
  ],
};

export const BUKIETY_SEO_CONFIG: CategorySeoShowcaseConfig = {
  sectionAriaLabel: 'Bukiety na każdą okazję',
  eyebrow: 'O bukietach',
  heading: 'Bukiety na każdą okazję',
  subtitle: [
    'Tworzymy bukiety pełne świeżości, harmonii kolorów i emocji.',
    'Wybierz kompozycję idealną na wyjątkową okazję lub podaruj bliskiej osobie bez okazji.',
  ],
  cardMinWidth: 220,
  cardBoxShadow: '0 2px 12px rgba(42,31,20,0.06)',
  cards: [
    {
      Icon: Flower2,
      title: ['Bukiety', 'na urodziny'],
      desc: 'Radosne i kolorowe kompozycje, które sprawią uśmiech i uczynią ten dzień wyjątkowym.',
      image: `${BASE}bouquet-birthday.png`,
      imageAlt: 'Bukiety na urodziny',
      linkLabel: 'Zobacz bukiety urodzinowe',
      href: collectionUrl('urodziny'),
    },
    {
      Icon: Heart,
      title: ['Bukiety', 'na rocznicę'],
      desc: 'Eleganckie kompozycje, które wyrażą miłość, bliskość i najpiękniejsze emocje.',
      image: `${BASE}bouquet-roses-red.png`,
      imageAlt: 'Bukiety na rocznicę',
      linkLabel: 'Zobacz bukiety na rocznicę',
      href: collectionUrl('rocznica'),
    },
    {
      Icon: User,
      title: ['Bukiety', 'dla Mamy'],
      desc: 'Delikatne i pełne ciepła bukiety dla mamy, babci lub innej bliskiej osoby.',
      image: `${BASE}bouquet-premium.png`,
      imageAlt: 'Bukiety dla Mamy',
      linkLabel: 'Zobacz bukiety dla Mamy',
      href: collectionUrl('dla-mamy'),
    },
    {
      Icon: Gift,
      title: ['Bukiety', 'z róż'],
      desc: 'Ponadczasowa klasyka – róże w wyjątkowych kompozycjach na każdą okazję.',
      image: `${BASE}bouquet-duzy.png`,
      imageAlt: 'Bukiety z róż',
      linkLabel: 'Zobacz bukiety z róż',
      href: collectionUrl('roze'),
    },
  ],
  pillsLabel: 'Najczęściej wybierane bukiety',
  pills: [
    { Icon: Flower2, label: 'Bukiety na urodziny', href: collectionUrl('urodziny') },
    { Icon: Heart, label: 'Bukiety na rocznicę', href: collectionUrl('rocznica') },
    { Icon: User, label: 'Bukiety dla Mamy', href: collectionUrl('dla-mamy') },
    { Icon: Gift, label: 'Bukiety z róż', href: collectionUrl('roze') },
    { Icon: Sparkles, label: 'Bukiety mieszane', href: collectionUrl('bukiety') },
    { Icon: Gift, label: 'Wszystkie bukiety', href: collectionUrl('bukiety') },
  ],
};
